import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    if (id) {
      const found = getComicById(id);
      setComic(found);
      if (found) {
        document.title = `${found.title} - 香香漫画韩国漫画免费阅读`;
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Header />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto py-20 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 5);

  const statusColors: Record<string, string> = {
    '连载中': 'bg-accent-100 text-accent-700',
    '已完结': 'bg-secondary-100 text-secondary-700',
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: comic.categories[0] === 'romance' ? '恋爱' : comic.categories[0] === 'fantasy' ? '奇幻' : comic.categories[0] === 'action' ? '动作' : comic.categories[0] === 'drama' ? '剧情' : comic.categories[0] === 'comedy' ? '搞笑' : comic.categories[0] === 'thriller' ? '悬疑' : comic.categories[0] === 'school' ? '校园' : '全部', href: `/category/${comic.categories[0]}` },
                { label: comic.title },
              ]}
            />
          </div>

          {/* Comic Header */}
          <div className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-[200px] md:w-[240px] mx-auto md:mx-0">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画免费阅读`}
                    title={`${comic.title} - 香香漫画韩国漫画免费阅读`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line text-xs"></i> {comic.author}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    <strong className="text-foreground-900">{comic.rating}</strong>
                    <span>({formatViews(comic.ratingCount)}评)</span>
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span>{formatViews(comic.views)}次阅读</span>
                  <span className="text-foreground-300">|</span>
                  <span>{formatViews(comic.likes)}人喜欢</span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors[comic.status] || 'bg-secondary-100 text-secondary-700'}`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500">
                    共 {comic.totalChapters} 话
                  </span>
                  <span className="text-sm text-foreground-500">
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200/70 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap">
                    <i className="ri-play-line mr-1.5"></i>开始阅读
                  </button>
                  <button className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap border border-background-200/70">
                    <i className="ri-heart-line mr-1.5"></i>加入收藏
                  </button>
                  <button className="w-11 h-11 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 hover:text-foreground-700 transition-all duration-200 border border-background-200/70">
                    <i className="ri-share-forward-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs: Info / Chapters */}
          <div className="border-b border-background-200/70 mb-8">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品详情
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表 ({comic.totalChapters})
              </button>
            </div>
          </div>

          {/* Content */}
          {activeTab === 'info' ? (
            <div className="pb-10">
              <article className="prose-custom max-w-3xl">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">作品简介</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3 bg-background-100 rounded-xl p-6 border border-background-200/70">
                  {comic.longDescription.split('\n').map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              </article>
            </div>
          ) : (
            <div className="pb-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {comic.chapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 group"
                  >
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors duration-200">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400">{ch.createdAt}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Related Comics */}
          <section className="py-10 md:py-14 border-t border-background-200/70">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">相关推荐</h2>
              <Link
                to={`/category/${comic.categories[0]}`}
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        </div>

        {/* FAQ Schema for comic */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': `${comic.title} - 香香漫画韩国漫画免费阅读`,
          'author': { '@type': 'Person', 'name': comic.author },
          'description': comic.description,
          'datePublished': comic.createdAt,
          'dateModified': comic.updatedAt,
          'publisher': {
            '@type': 'Organization',
            'name': '香香漫画',
            'url': 'https://xiangxiangcomic.com/',
          },
        }) }} />
      </main>
      <Footer />
    </>
  );
}